import { ClassroomQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { createDayjs } from "@outschool/time";
import {
  ClassPostListFragments,
  ClassPostList_ClassroomFragment,
  ClassPostList_LearnerFragment,
  ClassPostList_MeetingFragment,
  ClassPostType,
  ClassPost_ClassPostFragment,
} from "@outschool/ui-components-classroom";
import { Container, Select } from "@outschool/ui-legacy-component-library";
import React from "react";

import ClassPost from "./ClassPost";
import MeetingPost from "./MeetingPost";

type ClassPostListProps = {
  classroom: ClassPostList_ClassroomFragment;
  learner: ClassPostList_LearnerFragment;
  usersCanComment: boolean;
  showLessonUI?: boolean;
  addClassPostComment: (
    postUid: string,
    text: string,
    attachments?: any,
    video?: any,
    mentions?: string[],
  ) => Promise<void>;
  deleteClassPostComment: (
    commentUid: string,
    postUid: string,
  ) => Promise<void>;
  updateClassPost: (
    post: ClassPostType,
    text: string,
    textMimeType: string,
    attachments: ClassroomQuery["classroom"]["posts"][0]["messageContent"]["attachments"],
    video: ClassroomQuery["classroom"]["posts"][0]["messageContent"]["video"],
    publishAt: string,
  ) => Promise<void>;
};

type ClassOrMeetingPost =
  | ClassPost_ClassPostFragment
  | ClassPostList_MeetingFragment;

function sortClassOrMeetingPost(a: ClassOrMeetingPost, b: ClassOrMeetingPost) {
  const fieldA =
    a.__typename === "ClassPost"
      ? a.publishAt
      : a.__typename === "Meeting"
      ? a.start_time
      : undefined;
  const fieldB =
    b.__typename === "ClassPost"
      ? b.publishAt
      : b.__typename === "Meeting"
      ? b.start_time
      : undefined;
  return createDayjs(fieldB).diff(fieldA);
}

export default function ClassPostList({
  classroom,
  learner,
  usersCanComment,
  showLessonUI = false,
  addClassPostComment,
  deleteClassPostComment,
  updateClassPost,
}: ClassPostListProps) {
  const [unpublishedPostsView, setUnpublishedPostsView] = React.useState(false);

  const {
    posts,
    unpublishedPosts,
    activity,
    section,
    mentionNames,
    usersCanPost,
  } = classroom;
  const showMeetingPosts = !unpublishedPostsView && !showLessonUI;

  const canManage = section.currentUserCanManage;
  const meetingPastOrStartingSoon = React.useMemo(() => {
    const meetings = section?.meetings;
    if (!showMeetingPosts || !meetings) {
      return [];
    }
    return meetings.filter(meeting =>
      createDayjs(meeting.start_time).isBefore(createDayjs().add(1, "hour")),
    );
  }, [section, showMeetingPosts]);
  const allPosts = React.useMemo(() => {
    const postsRaw = [
      ...(unpublishedPostsView ? unpublishedPosts : posts),
      ...meetingPastOrStartingSoon,
    ];
    return showLessonUI ? postsRaw : postsRaw.sort(sortClassOrMeetingPost);
  }, [
    unpublishedPostsView,
    posts,
    unpublishedPosts,
    meetingPastOrStartingSoon,
    showLessonUI,
  ]);
  const { t } = useTranslation("client\\components\\classroom\\ClassPostList");
  return (
    <React.Fragment>
      <Container
        size="small"
        sx={{ paddingX: 0, paddingBottom: "large", textAlign: "right" }}
      >
        {canManage && (
          <React.Fragment>
            {t("Showing: ")}
            <Select
              options={[t("Published"), t("Unpublished")]}
              onChange={value =>
                setUnpublishedPostsView(value === t("Unpublished"))
              }
            />
          </React.Fragment>
        )}
      </Container>
      {allPosts.map(classOrMeetingPost =>
        classOrMeetingPost.__typename === "ClassPost" ? (
          <ClassPost
            key={classOrMeetingPost.uid}
            post={classOrMeetingPost}
            section={section}
            activity={activity}
            learner={learner}
            canManage={canManage}
            usersCanPost={usersCanPost}
            usersCanComment={!unpublishedPostsView && usersCanComment}
            showSenderPhoto={true}
            onComment={addClassPostComment}
            onDeleteComment={deleteClassPostComment}
            updateClassPost={updateClassPost}
            mentionNames={mentionNames}
          />
        ) : (
          <MeetingPost
            key={classOrMeetingPost.uid}
            activity={activity}
            section={section}
            // @ts-ignore TS(2322): Type '{ __typename?: "ClassPost" | undefined; uid:... Remove this comment to see the full error message
            meeting={classOrMeetingPost}
            learner={learner}
            canManage={canManage}
          />
        ),
      )}
    </React.Fragment>
  );
}

ClassPostList.fragments = ClassPostListFragments;
